/**
 * A live Mental Map room — the client half of migration 808.
 *
 * The link's token is the access: every call is a token-scoped SECURITY DEFINER
 * function, so the same code serves a signed-in host and a name-only guest.
 * "Who am I" on return is the per-link browser secret from `guestParticipant`
 * (the session guest secret), so a refresh keeps your map.
 *
 * Other people's maps arrive by polling: an anonymous browser holds no table
 * rights, so there is nothing for it to subscribe to.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getOrCreateGuestSecret } from '@/lib/guestParticipant';
import { getSafeSupabaseClient } from '@/lib/supabaseSync';
import { nextTimerEnd } from '@/lib/sessions/roomRules';
import type { MapRoom, MapRoomPerson } from './MentalMapRoom';
import { EMPTY_MAP, parseMapDoc, parseSharedModel, type MapDoc, type SharedModel } from './model';
import type { NewSharedModel, SharedRoom } from './SharedModels';

const POLL_MS = 3000;
const SAVE_DELAY_MS = 700;
const NAME_KEY = 'pp_mental_map_name';

export interface RoomSnapshot {
  prompt: string;
  revealed: boolean;
  isHost: boolean;
  /** Null until this browser has joined. */
  meId: string | null;
  people: { id: string; name: string; shown: boolean; done: boolean; started: boolean; doc: MapDoc | null }[];
  models: SharedModel[];
  /** Seconds left on the host's countdown, by the server's clock; null when none is running. */
  secondsLeft: number | null;
}

export interface RoomResult<T> {
  ok: boolean;
  data?: T;
  /** Plain words, safe to show — the functions raise readable messages. */
  error?: string;
}

/** Turn whatever the read function returned into a snapshot, trusting none of it. */
export function parseRoomSnapshot(value: unknown): RoomSnapshot | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.prompt !== 'string') return null;
  const people: RoomSnapshot['people'] = [];
  for (const p of Array.isArray(raw.people) ? raw.people : []) {
    if (!p || typeof p !== 'object') continue;
    const r = p as Record<string, unknown>;
    if (typeof r.id !== 'string' || typeof r.name !== 'string') continue;
    people.push({
      id: r.id,
      name: r.name,
      shown: r.shown === true,
      done: r.done === true,
      started: r.started === true,
      doc: r.doc && typeof r.doc === 'object' ? parseMapDoc(r.doc) : null,
    });
  }
  const models: SharedModel[] = [];
  for (const m of Array.isArray(raw.models) ? raw.models : []) {
    const model = parseSharedModel(m);
    if (model) models.push(model);
  }
  return {
    models,
    secondsLeft: typeof raw.seconds_left === 'number' && raw.seconds_left >= 0 ? raw.seconds_left : null,
    prompt: raw.prompt,
    revealed: raw.revealed === true,
    isHost: raw.is_host === true,
    meId: typeof raw.me_id === 'string' ? raw.me_id : null,
    people,
  };
}

async function call<T>(fn: string, args: Record<string, unknown>, read: (data: unknown) => T | null): Promise<RoomResult<T>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { data, error } = await supabase.rpc(fn, args);
  if (error) return { ok: false, error: error.message || 'Something went wrong.' };
  const parsed = read(data);
  return parsed === null ? { ok: false, error: 'Something went wrong.' } : { ok: true, data: parsed };
}

/** Open a new room. Signed-in only; returns the token that goes in the link. */
export function createMentalMapRoom(prompt: string): Promise<RoomResult<{ joinToken: string }>> {
  return call('create_mental_map_room', { p_prompt: prompt }, (data) => {
    const token = (data as { join_token?: unknown } | null)?.join_token;
    return typeof token === 'string' ? { joinToken: token } : null;
  });
}

export function mentalMapJoinUrl(origin: string, token: string): string {
  return `${origin}/map/${token}`;
}

export function fetchMentalMapRoom(token: string): Promise<RoomResult<RoomSnapshot>> {
  return call('get_mental_map_room_with_token', { p_token: token, p_secret: getOrCreateGuestSecret(token) }, parseRoomSnapshot);
}

export function joinMentalMapRoom(token: string, name: string): Promise<RoomResult<true>> {
  return call(
    'join_mental_map_with_token',
    { p_token: token, p_secret: getOrCreateGuestSecret(token), p_display_name: name.trim() },
    () => true as const,
  );
}

export function saveMentalMap(token: string, doc: MapDoc, shown: boolean): Promise<RoomResult<true>> {
  return call(
    'save_mental_map_with_token',
    { p_token: token, p_secret: getOrCreateGuestSecret(token), p_doc: doc, p_shown: shown },
    () => true as const,
  );
}

export function revealMentalMapRoom(token: string): Promise<RoomResult<true>> {
  return call('reveal_mental_map_room', { p_token: token }, () => true as const);
}

export function createSharedModel(token: string, input: NewSharedModel): Promise<RoomResult<string>> {
  return call(
    'create_mental_map_model_with_token',
    {
      p_token: token,
      p_secret: getOrCreateGuestSecret(token),
      p_title: input.title,
      p_level: input.level,
      p_parent_model_id: input.parentModelId ?? null,
      p_parent_shape_id: input.parentShapeId ?? null,
    },
    (data) => {
      const id = (data as { model_id?: unknown } | null)?.model_id;
      return typeof id === 'string' ? id : null;
    },
  );
}

export function takeSharedModelPen(token: string, modelId: string): Promise<RoomResult<MapDoc>> {
  return call(
    'take_mental_map_pen_with_token',
    { p_token: token, p_secret: getOrCreateGuestSecret(token), p_model_id: modelId },
    (data) => parseMapDoc((data as { doc?: unknown } | null)?.doc),
  );
}

export function saveSharedModel(token: string, modelId: string, doc: MapDoc): Promise<RoomResult<true>> {
  return call(
    'save_mental_map_model_with_token',
    { p_token: token, p_secret: getOrCreateGuestSecret(token), p_model_id: modelId, p_doc: doc, p_title: null },
    () => true as const,
  );
}

export interface MyRoom {
  prompt: string;
  joinToken: string;
  createdAt: string;
}

/** The rooms the signed-in person has opened, newest first — so a room can be found again. */
export async function listMyMentalMapRooms(): Promise<RoomResult<MyRoom[]>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { data, error } = await supabase
    .from('mental_map_rooms')
    .select('prompt, join_token, created_at')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return { ok: false, error: error.message };
  const rooms: MyRoom[] = [];
  for (const row of (data ?? []) as { prompt?: unknown; join_token?: unknown; created_at?: unknown }[]) {
    if (typeof row.prompt === 'string' && typeof row.join_token === 'string' && typeof row.created_at === 'string') {
      rooms.push({ prompt: row.prompt, joinToken: row.join_token, createdAt: row.created_at });
    }
  }
  return { ok: true, data: rooms };
}

export function setMentalMapDone(token: string, done: boolean): Promise<RoomResult<true>> {
  return call('set_mental_map_done_with_token', { p_token: token, p_secret: getOrCreateGuestSecret(token), p_done: done }, () => true as const);
}

/** Host only: start a countdown, or stop it with null. */
export function setMentalMapTimer(token: string, seconds: number | null): Promise<RoomResult<true>> {
  return call('set_mental_map_timer', { p_token: token, p_seconds: seconds }, () => true as const);
}

export function rememberedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export type LiveRoomState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  /** The link is good; this browser has not joined yet. */
  | { status: 'needs_name'; prompt: string; join: (name: string) => Promise<string | null> }
  | { status: 'ready'; room: MapRoom; saveError: string | null };

export function useLiveRoom(token: string): LiveRoomState {
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // How many changes are on their way to the room (mine, or a shared model's).
  const [pendingSaves, setPendingSaves] = useState(0);

  // My own map lives here once loaded: the server copy is only its first value.
  const [myDoc, setMyDoc] = useState<MapDoc | null>(null);
  const [myMapShown, setMyMapShownState] = useState(false);
  const [myDone, setMyDoneState] = useState(false);
  // The countdown's end as a time on THIS device, so it ticks smoothly between polls.
  const [timerEndsAt, setTimerEndsAt] = useState<number | null>(null);

  const latest = useRef<{ doc: MapDoc; shown: boolean }>({ doc: EMPTY_MAP, shown: false });
  const savedJson = useRef<string>('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    const result = await fetchMentalMapRoom(token);
    if (!result.ok || !result.data) {
      // A failed poll after a good load is a blip, not a reason to drop the room.
      setLoadError((prev) => prev ?? result.error ?? 'Could not open this room.');
      return;
    }
    setLoadError(null);
    setSnapshot(result.data);
    const left = result.data.secondsLeft;
    setTimerEndsAt((current) => nextTimerEnd(current, left));
  }, [token]);

  useEffect(() => {
    void refresh();
    // Keep polling in a background tab too: in a meeting the room is often
    // behind the call window, and it must be current the moment it comes back.
    const id = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // First snapshot after joining: adopt the stored map as mine.
  useEffect(() => {
    if (myDoc !== null || !snapshot?.meId) return;
    const mine = snapshot.people.find((p) => p.id === snapshot.meId);
    const doc = mine?.doc ?? EMPTY_MAP;
    latest.current = { doc, shown: mine?.shown ?? false };
    savedJson.current = JSON.stringify(latest.current);
    setMyDoc(doc);
    setMyMapShownState(mine?.shown ?? false);
    setMyDoneState(mine?.done ?? false);
  }, [snapshot, myDoc]);

  const flush = useCallback(async () => {
    timer.current = null;
    const json = JSON.stringify(latest.current);
    if (json === savedJson.current) return;
    setPendingSaves((n) => n + 1);
    const result = await saveMentalMap(token, latest.current.doc, latest.current.shown);
    setPendingSaves((n) => n - 1);
    if (result.ok) {
      savedJson.current = json;
      setSaveError(null);
    } else {
      setSaveError(result.error ?? 'Your map could not be saved.');
    }
  }, [token]);

  const schedule = useCallback(
    (delay: number) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), delay);
    },
    [flush],
  );

  // Leaving the page with a change still waiting: send it now.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void flush();
      }
    },
    [flush],
  );

  const saveMyDoc = useCallback(
    (doc: MapDoc) => {
      latest.current = { ...latest.current, doc };
      setMyDoc(doc);
      schedule(SAVE_DELAY_MS);
    },
    [schedule],
  );

  const setMyMapShown = useCallback(
    (shown: boolean) => {
      latest.current = { ...latest.current, shown };
      setMyMapShownState(shown);
      schedule(0);
    },
    [schedule],
  );

  // Shared models: the pen holder's edits, saved the same way as a person's own map.
  const modelPending = useRef<{ modelId: string; doc: MapDoc } | null>(null);
  const modelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushModel = useCallback(async () => {
    if (modelTimer.current) clearTimeout(modelTimer.current);
    modelTimer.current = null;
    const pending = modelPending.current;
    if (!pending) return;
    modelPending.current = null;
    setPendingSaves((n) => n + 1);
    const result = await saveSharedModel(token, pending.modelId, pending.doc);
    setPendingSaves((n) => n - 1);
    setSaveError(result.ok ? null : (result.error ?? 'The shared model could not be saved.'));
    // Losing the pen is the usual reason: show who has it now.
    if (!result.ok) void refresh();
  }, [token, refresh]);

  useEffect(
    () => () => {
      void flushModel();
    },
    [flushModel],
  );

  const shared = useMemo<SharedRoom>(
    () => ({
      models: [],
      saveModel: (modelId, doc) => {
        // A different model with a save still waiting goes out first.
        if (modelPending.current && modelPending.current.modelId !== modelId) void flushModel();
        modelPending.current = { modelId, doc };
        if (modelTimer.current) clearTimeout(modelTimer.current);
        modelTimer.current = setTimeout(() => void flushModel(), SAVE_DELAY_MS);
      },
      createModel: async (input) => {
        // A deeper model names a shape in its parent, so the parent must be saved first.
        await flushModel();
        const result = await createSharedModel(token, input);
        if (!result.ok || !result.data) {
          setSaveError(result.error ?? 'The model could not be added.');
          return null;
        }
        await refresh();
        return result.data;
      },
      takePen: async (modelId) => {
        const result = await takeSharedModelPen(token, modelId);
        if (!result.ok || !result.data) {
          setSaveError(result.error ?? 'Could not take the pen.');
          return null;
        }
        await refresh();
        return result.data;
      },
    }),
    [token, refresh, flushModel],
  );

  const setMyDone = useCallback(
    (done: boolean) => {
      setMyDoneState(done);
      void setMentalMapDone(token, done).then((result) => {
        if (!result.ok) setSaveError(result.error ?? 'That could not be sent to the room.');
        void refresh();
      });
    },
    [token, refresh],
  );

  const setTimer = useCallback(
    (seconds: number | null) => {
      void setMentalMapTimer(token, seconds).then((result) => {
        if (!result.ok) setSaveError(result.error ?? 'The timer could not be set.');
        void refresh();
      });
    },
    [token, refresh],
  );

  const revealAll = useCallback(() => {
    void revealMentalMapRoom(token).then((result) => {
      if (!result.ok) setSaveError(result.error ?? 'The maps could not be revealed.');
      void refresh();
    });
  }, [token, refresh]);

  const join = useCallback(
    async (name: string): Promise<string | null> => {
      const result = await joinMentalMapRoom(token, name);
      if (!result.ok) return result.error ?? 'Could not join this room.';
      try {
        localStorage.setItem(NAME_KEY, name.trim());
      } catch {
        // Storage blocked: the name simply is not remembered for next time.
      }
      await refresh();
      return null;
    },
    [token, refresh],
  );

  // A poll returns fresh objects for maps that have not changed; hand back the
  // same object for an unchanged map so the stage does not redraw every poll.
  const docCache = useRef(new Map<string, { json: string; doc: MapDoc }>());
  const people = useMemo<MapRoomPerson[]>(() => {
    if (!snapshot) return [];
    return snapshot.people.map((p) => {
      if (p.id === snapshot.meId) {
        return {
          id: p.id,
          name: p.name,
          doc: snapshot.revealed || myMapShown ? (myDoc ?? EMPTY_MAP) : null,
          done: myDone,
          started: (myDoc?.shapes.length ?? 0) > 0,
        };
      }
      const status = { done: p.done, started: p.started };
      if (p.doc === null) return { id: p.id, name: p.name, doc: null, ...status };
      const json = JSON.stringify(p.doc);
      const cached = docCache.current.get(p.id);
      if (cached?.json === json) return { id: p.id, name: p.name, doc: cached.doc, ...status };
      docCache.current.set(p.id, { json, doc: p.doc });
      return { id: p.id, name: p.name, doc: p.doc, ...status };
    });
  }, [snapshot, myDoc, myMapShown, myDone]);

  const modelCache = useRef(new Map<string, { json: string; model: SharedModel }>());
  const models = useMemo<SharedModel[]>(() => {
    if (!snapshot) return [];
    return snapshot.models.map((m) => {
      const json = JSON.stringify(m);
      const cached = modelCache.current.get(m.id);
      if (cached?.json === json) return cached.model;
      modelCache.current.set(m.id, { json, model: m });
      return m;
    });
  }, [snapshot]);

  if (!snapshot) {
    return loadError ? { status: 'error', message: loadError } : { status: 'loading' };
  }
  if (!snapshot.meId) {
    return { status: 'needs_name', prompt: snapshot.prompt, join };
  }
  if (myDoc === null) return { status: 'loading' };

  return {
    status: 'ready',
    saveError,
    room: {
      prompt: snapshot.prompt,
      meId: snapshot.meId,
      isHost: snapshot.isHost,
      myDoc,
      myMapShown,
      revealed: snapshot.revealed,
      people,
      shared: { ...shared, models },
      saveState: saveError ? 'failed' : pendingSaves > 0 ? 'saving' : 'saved',
      myDone,
      setMyDone,
      timerEndsAt,
      setTimer,
      joinUrl: typeof window === 'undefined' ? undefined : mentalMapJoinUrl(window.location.origin, token),
      saveMyDoc,
      setMyMapShown,
      revealAll,
    },
  };
}
